export const getStatusClass = (status) => {
  switch (status) {
    case "Approved":
      return "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300";
    case "Rejected":
      return "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300";
    default:
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300";
  }
};

export const truncate = (text, len = 58) =>
  text && text.length > len ? `${text.slice(0, len)}...` : text;

const firstNonEmpty = (...values) =>
  values
    .map((value) => String(value || "").trim())
    .find(Boolean) || "";

export const getTravelQuotationQtNumber = (item, quotation) =>
  firstNonEmpty(
    quotation?.runNumber,
    quotation?.quotationNumber,
    item?.quotationNo,
    item?.quotationNumber
  );

export const getTravelQuotationDisplayLabel = (item, quotation) =>
  firstNonEmpty(
    quotation?.documentNo,
    item?.documentNo,
    item?.quotationDisplayNumber,
    quotation?.quotationNumber,
    item?.quotationNumber,
    item?.quotationNo
  ) || "-";

export const getTravelQuotationProjectLabel = (item, quotation) =>
  firstNonEmpty(
    quotation?.projectName,
    quotation?.title,
    quotation?.projectTitle,
    item?.projectName,
    item?.quotationTitle,
    item?.quotationName,
    item?.documentNo,
    item?.quotationDisplayNumber
  ) || "-";

export const normalizePlaceText = (value) =>
  String(value || "")
    .replace(/\s*,\s*/g, ", ")
    .replace(/\s*\n\s*/g, "\n")
    .replace(/[ \t]+/g, " ")
    .trim();

export const extractPrimaryPlaceName = (value) => {
  const text = normalizePlaceText(value);
  if (!text) return "-";

  const firstSegment =
    text.split(",").map((part) => part.trim()).find(Boolean) || text;

  const stopTerms = [
    "สำนักงานใหญ่",
    "สาขา",
    "ถนน",
    "แขวง",
    "เขต",
    "อำเภอ",
    "ตำบล",
    "จังหวัด",
    "กรุงเทพมหานคร",
    "Tower",
    "Building",
  ];

  const cutIndex = stopTerms.reduce((earliest, term) => {
    const index = firstSegment.indexOf(term);
    if (index <= 0) return earliest;
    if (earliest === -1) return index;
    return Math.min(earliest, index);
  }, -1);

  if (cutIndex > 0) {
    return firstSegment.slice(0, cutIndex).trim() || firstSegment;
  }

  return firstSegment || text;
};

export const extractPrimaryCompanyName = (value) => {
  const text = normalizePlaceText(value);
  if (!text) return "-";

  const firstSegment =
    text.split(",").map((part) => part.trim()).find(Boolean) || text;

  const stopTerms = [
    "(สำนักงานใหญ่)",
    "สำนักงานใหญ่",
    "(สาขา",
    "สาขา",
    "ถนน",
    "แขวง",
    "เขต",
    "อำเภอ",
    "ตำบล",
    "จังหวัด",
    "กรุงเทพมหานคร",
  ];

  const cutIndex = stopTerms.reduce((earliest, term) => {
    const index = firstSegment.indexOf(term);
    if (index <= 0) return earliest;
    if (earliest === -1) return index;
    return Math.min(earliest, index);
  }, -1);

  const companyName =
    cutIndex > 0 ? firstSegment.slice(0, cutIndex).trim() : firstSegment;

  return companyName.replace(/\s+\($/, "").trim() || firstSegment;
};

export const padThaiTextForPdf = (value) => {
  const text = String(value || "").trim();
  if (!text) return "-";

  return /[ก-๙]/.test(text) ? `${text} ` : text;
};

export const getReceiptUrls = (item) => {
  const tollReceiptUrls = Array.isArray(item?.tollReceiptUrls)
    ? item.tollReceiptUrls
    : [];

  const urls = [...tollReceiptUrls];

  if (item?.receiptUrl && !urls.includes(item.receiptUrl)) {
    urls.unshift(item.receiptUrl);
  }

  return urls.filter(Boolean);
};

export const getFileType = (url) => {
  const normalizedUrl = String(url || "").toLowerCase();
  if (normalizedUrl.endsWith(".pdf")) return "pdf";
  return "image";
};

export const getTravelAmount = (item) => Number(item?.amount || 0);

export const getTollAmount = (item) => Number(item?.tollFee || 0);

export const getTotalAmount = (item) =>
  getTravelAmount(item) + getTollAmount(item);

export const formatMoney = (value) =>
  Number(value || 0).toLocaleString("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const formatDepartureDateTime = (value) => {
  if (!value) return "-";

  return new Date(value).toLocaleString("th-TH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatApprovalActedAtThai = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Bangkok",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const parts = formatter.formatToParts(date);
  const getPart = (type) => parts.find((part) => part.type === type)?.value || "";

  const day = getPart("day");
  const month = getPart("month");
  const year = String(Number(getPart("year")) + 543);
  const hour = getPart("hour");
  const minute = getPart("minute");

  return `${day}/${month}/${year} เวลา ${hour}:${minute}`;
};

const STEP_STATUS = {
  ACTIVE: "active",
  APPROVED: "approved",
  REJECTED: "rejected",
  INACTIVE: "inactive",
};

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const normalizeStepStatus = (status, isCurrent = false) => {
  const normalized = normalizeText(status);

  if (
    normalized === "approved" ||
    normalized === "done" ||
    normalized === "complete" ||
    normalized === "completed"
  ) {
    return STEP_STATUS.APPROVED;
  }

  if (
    normalized === "rejected" ||
    normalized === "reject" ||
    normalized === "denied"
  ) {
    return STEP_STATUS.REJECTED;
  }

  if (
    normalized === "pending" ||
    normalized === "waiting" ||
    normalized === "in_progress" ||
    normalized === "active" ||
    normalized === "current"
  ) {
    return isCurrent ? STEP_STATUS.ACTIVE : STEP_STATUS.INACTIVE;
  }

  return isCurrent ? STEP_STATUS.ACTIVE : STEP_STATUS.INACTIVE;
};

export const getRequesterLevel = (item) =>
  Number(
    item?.requestedByLevel ||
      item?.requestedLevel ||
      item?.requesterLevel ||
      item?.requestLevel ||
      item?.createdByLevel ||
      item?.requestedByUser?.level ||
      item?.requester?.level ||
      item?.user?.level ||
      0
  );

export const getCurrentApprovalLevel = (item) =>
  Number(
    item?.currentApprovalLevel ||
      item?.currentApproverLevel ||
      item?.pendingApprovalLevel ||
      item?.nextApprovalLevel ||
      item?.currentStepLevel ||
      item?.currentApprovalStep?.level ||
      0
  );

const getRawApprovalSteps = (item) => {
  const candidates = [
    item?.approvalSteps,
    item?.approvalFlow,
    item?.approvalHierarchy?.[0]?.approvalHierarchy,
    item?.approvalHierarchy,
  ];

  return (
    candidates.find(
      (steps) =>
        Array.isArray(steps) &&
        steps.some(
          (step) =>
            typeof step === "object" &&
            step !== null &&
            (step.level != null ||
              step.approvalLevel != null ||
              step.approverLevel != null)
        )
    ) || []
  );
};

const fallbackLevels = (item) => {
  const requesterLevel = getRequesterLevel(item);
  return requesterLevel >= 3 ? [4] : [3, 4];
};

export const getApprovalFlowSteps = (item) => {
  const rawSteps = getRawApprovalSteps(item);
  const currentLevel = getCurrentApprovalLevel(item);
  const overallStatus = normalizeText(item?.status || item?.approvalStatus);
  const waitingLabel = "รออนุมัติ";

  if (rawSteps.length > 0) {
    return rawSteps
      .map((step, index) => {
        const level = Number(
          step?.level ?? step?.approvalLevel ?? step?.approverLevel ?? 0
        );
        const isCurrent =
          Boolean(step?.isCurrent || step?.active) ||
          (currentLevel > 0 && level === currentLevel);

        return {
          key: `${level || "step"}-${index}`,
          level,
          label: `Lv.${level || "?"}`,
          approver: firstNonEmpty(
            step?.approver,
            step?.email,
            step?.username
          ),
          status: normalizeStepStatus(step?.status, isCurrent),
          actedAtLabel:
            normalizeStepStatus(step?.status, isCurrent) === STEP_STATUS.APPROVED
              ? formatApprovalActedAtThai(
                  step?.actedAt ||
                    step?.approvedAt ||
                    step?.updatedAt ||
                    step?.actionAt
                )
              : waitingLabel,
        };
      })
      .filter((step) => step.level > 0);
  }

  const levels = fallbackLevels(item);

  if (overallStatus === "approved") {
    return levels.map((level) => ({
      key: `lv-${level}`,
      level,
      label: `Lv.${level}`,
      approver: "",
      status: STEP_STATUS.APPROVED,
      actedAtLabel: waitingLabel,
    }));
  }

  const rejectedLevel = currentLevel || levels[0];

  if (overallStatus === "rejected") {
    return levels.map((level) => ({
      key: `lv-${level}`,
      level,
      label: `Lv.${level}`,
      approver: "",
      status:
        level < rejectedLevel
          ? STEP_STATUS.APPROVED
          : level === rejectedLevel
          ? STEP_STATUS.REJECTED
          : STEP_STATUS.INACTIVE,
      actedAtLabel: waitingLabel,
    }));
  }

  const activeLevel = currentLevel || levels[0];

  return levels.map((level) => ({
    key: `lv-${level}`,
    level,
    label: `Lv.${level}`,
    approver: "",
    status:
      level < activeLevel
        ? STEP_STATUS.APPROVED
        : level === activeLevel
        ? STEP_STATUS.ACTIVE
        : STEP_STATUS.INACTIVE,
    actedAtLabel: waitingLabel,
  }));
};

export const getApprovalSummary = (item) => {
  const steps = getApprovalFlowSteps(item);
  const activeStep = steps.find((step) => step.status === STEP_STATUS.ACTIVE);
  const rejectedStep = steps.find((step) => step.status === STEP_STATUS.REJECTED);
  const overallStatus = normalizeText(item?.status || item?.approvalStatus);

  if (overallStatus === "approved") {
    return "อนุมัติครบทุกขั้น";
  }

  if (rejectedStep) {
    return `ไม่อนุมัติที่ ${rejectedStep.label}`;
  }

  if (activeStep) {
    return `รออนุมัติ ${activeStep.label}`;
  }

  return item?.status || item?.approvalStatus || "-";
};
